import { Headphones, TrendingUp, Clock, ChartBar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import type { PodWorkspace } from "@/lib/podWorkspace/podWorkspace.service";
import type { Podcast } from "@/lib/podcast/podcast.service";

interface StatisticsSectionProps {
	workspaces: PodWorkspace[];
	podcastsByWorkspace: Record<string, Podcast | null>;
	listenedTimeStats: { formattedTime: string };
	isLoadingListenedTime: boolean;
}

/**
 * Statistics section component displaying key metrics:
 * - Total listened time
 * - Total podspaces
 * - Completed podcasts
 */
export const StatisticsSection = ({
	workspaces,
	podcastsByWorkspace,
	listenedTimeStats,
	isLoadingListenedTime,
}: StatisticsSectionProps) => {
	const { t } = useTranslation();

	return (
		<div className="order-1 md:order-2 mb-5 md:mb-0">
			<h2 className="text-xs text-muted-foreground md:text-foreground md:text-2xl mb-3 md:mb-4 px-1">
				<div className="flex items-center gap-2">
					<ChartBar className="h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />{" "}
					{t("dashboard.statistics")}
				</div>
			</h2>
			<div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-3">
				<Card className="p-2.5 md:p-4 bg-white/[0.04] backdrop-blur-xl border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
					<div className="flex flex-col items-center text-center gap-1.5 md:flex-row md:items-center md:text-left md:gap-2.5">
						<div className="p-1.5 md:p-2 rounded-lg bg-purple-500/[0.08]">
							<Clock className="h-3.5 w-3.5 md:h-5 md:w-5 text-purple-400/70" />
						</div>
						<div>
							<p className="text-[10px] md:text-xs text-muted-foreground/70 leading-tight">
								{t("dashboard.listenedTime")}
							</p>
							<p className="text-base md:text-xl font-semibold leading-tight text-foreground/90">
								{isLoadingListenedTime
									? "..."
									: listenedTimeStats.formattedTime}
							</p>
						</div>
					</div>
				</Card>

				<Card className="p-2.5 md:p-4 bg-white/[0.04] backdrop-blur-xl border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
					<div className="flex flex-col items-center text-center gap-1.5 md:flex-row md:items-center md:text-left md:gap-2.5">
						<div className="p-1.5 md:p-2 rounded-lg bg-blue-500/[0.08]">
							<Headphones className="h-3.5 w-3.5 md:h-5 md:w-5 text-blue-400/70" />
						</div>
						<div>
							<p className="text-[10px] md:text-xs text-muted-foreground/70 leading-tight">
								{t("dashboard.totalPodspaces")}
							</p>
							<p className="text-base md:text-xl font-semibold leading-tight text-foreground/90">
								{workspaces.length}
							</p>
						</div>
					</div>
				</Card>

				<Card className="p-2.5 md:p-4 bg-white/[0.04] backdrop-blur-xl border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]">
					<div className="flex flex-col items-center text-center gap-1.5 md:flex-row md:items-center md:text-left md:gap-2.5">
						<div className="p-1.5 md:p-2 rounded-lg bg-green-500/[0.08]">
							<TrendingUp className="h-3.5 w-3.5 md:h-5 md:w-5 text-green-400/70" />
						</div>
						<div>
							<p className="text-[10px] md:text-xs text-muted-foreground/70 leading-tight">
								{t("dashboard.completedPodcasts")}
							</p>
							<p className="text-base md:text-xl font-semibold leading-tight text-foreground/90">
								{
									Object.values(podcastsByWorkspace).filter(
										(p) => p?.status === "COMPLETED",
									).length
								}
							</p>
						</div>
					</div>
				</Card>
			</div>
		</div>
	);
};
